import { useState } from "react";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount";
import { SectionHeader } from "@sprout/typography";
import { TextInput } from "@sprout/text_input";
import ProfileButtons from "@components/profile_buttons";
import UnloadHandler from "@components/unload_handler";
import useTranslation from "@hooks/use_translation";
import "./account_edit.scss";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import useGoBack from "@/hooks/use_go_back";
import axios, { isAxiosError } from "axios";
import IdentityVerificationProfileCard from "@/components/IdentityVerificationProfileCard";
import ConnectedAccountsCard, { type IdentityProvider } from "@/components/ConnectedAccountsCard";
import { useShowFlash } from "@sprout/flash.tsx";
import { useQueryClient } from "@tanstack/react-query";
type Props = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  identities: IdentityProvider[] | null;
  paths: {
    userPath: string;
    identitiesPath: string | null;
  };
};

const AccountEdit = ({ email, paths, identities, ...props }: Props) => {
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();

  const { t } = useTranslation("user");
  const goBack = useGoBack();
  const [firstName, setFirstName] = useState(props.firstName || "");
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastName, setLastName] = useState(props.lastName || "");
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  useEffectAfterMount(() => {
    setUnsavedChanges(true);
  }, [firstName, firstNameError, lastName, lastNameError]);

  const submit = async () => {
    setIsSubmitting(true);
    setError(null);
    setFirstNameError(null);
    setLastNameError(null);

    try {
      const formData = new FormData();

      formData.append("user[first_name]", firstName);
      formData.append("user[last_name]", lastName);

      await axios.patch(paths.userPath, formData);

      showFlash(t("profile_updated.success"), "success");
      setUnsavedChanges(false);
      queryClient.removeQueries({ queryKey: ["whoami"] });
      goBack();
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 422) {
        const { errors } = e.response.data;

        if (errors.first_name) {
          setFirstNameError(errors.first_name[0]);
        }

        if (errors.last_name) {
          setLastNameError(errors.last_name[0]);
        }
      } else {
        console.error(e);
        setError(t("defaultError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      {unsavedChanges && <UnloadHandler />}
      <BackButton href="/account" />
      <SectionHeader as="h1">{t("edit_account")}</SectionHeader>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2 items-start">
        <div className="account-inputs">
          <TextInput
            id="first-name"
            label={t("fields.firstName")}
            onChange={(event) => setFirstName(event.currentTarget.value || "")}
            maxLength={255}
            value={firstName}
            error={firstNameError && t("accountForm.firstNameError", { error: firstNameError })}
          />
          <TextInput
            id="last-name"
            label={t("fields.lastName")}
            onChange={(event) => setLastName(event.currentTarget.value || "")}
            maxLength={255}
            value={lastName}
            error={lastNameError && t("accountForm.lastNameError", { error: lastNameError })}
          />
          <TextInput
            id="email-address"
            label={t("fields.email")}
            onChange={() => {}}
            maxLength={255}
            value={email}
            disabled
          />
        </div>

        <div className="flex flex-col gap-8">
          <IdentityVerificationProfileCard />
          {identities &&
            paths.identitiesPath &&
            identities.some((identity) => identity.connected) && (
              <ConnectedAccountsCard
                identities={identities}
                identitiesPath={paths.identitiesPath}
              />
            )}
        </div>
      </div>

      <ProfileButtons loading={isSubmitting} error={error} submit={submit} paths={paths} />
    </Container>
  );
};

export default AccountEdit;
